import { env } from "@/lib/env"

const VERSION_MANIFEST_PATH = "/version.json"
const RELOAD_GUARD_KEY = "merrin.last-forced-reload"
const RELOAD_GUARD_WINDOW = 10_000

/**
 * Reads the build id of whatever is currently deployed. The cache-busting query
 * and `no-store` together are deliberate belt and braces: a CDN that ignores
 * one usually respects the other.
 */
export async function fetchDeployedBuildId(): Promise<string | null> {
  try {
    const response = await fetch(`${VERSION_MANIFEST_PATH}?t=${Date.now()}`, {
      cache: "no-store",
      headers: { "cache-control": "no-cache" },
    })

    if (!response.ok) {
      return null
    }

    const body = (await response.json()) as { buildId?: string }

    return body.buildId ?? null
  } catch {
    return null
  }
}

export function isStaleBuild(deployedBuildId: string | null): boolean {
  if (!deployedBuildId) {
    return false
  }

  return deployedBuildId !== env.buildId
}

/**
 * Reloads at most once per guard window. A chunk that 404s during the reload
 * itself would otherwise put the tab into a refresh loop.
 */
export function reloadOnce(): void {
  try {
    const lastReload = Number(sessionStorage.getItem(RELOAD_GUARD_KEY) ?? 0)

    if (Date.now() - lastReload < RELOAD_GUARD_WINDOW) {
      return
    }

    sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()))
  } catch {
    // Storage denied; a single reload attempt is still better than a dead page.
  }

  window.location.reload()
}
